import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useSucursal } from '../../utils/sucursalContext';
import AdminLayout from '../../components/layout/AdminLayout';

type VencimientoUsuario = {
  id: string;
  nombre: string;
  email: string;
  telefono: string;
  tipo_membresia: string;
  fecha_vencimiento: string;
  estado: string;
};

type ReservaHoy = {
  id: string;
  time: string;
  reserved: number;
  capacity: number;
  percentage: number;
};

type PlanItem = {
  name: string;
  value: number;
  color: string;
};

type Activity = {
  id: string;
  type: 'reservation' | 'payment' | 'user';
  user: string;
  action: string;
  time: string;
};

const PLAN_COLORS = ['#14b8a6', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#10b981'];

const getTimeAgo = (isoDate: string) => {
  const now = new Date();
  const date = new Date(isoDate);
  const diffMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

  if (diffMinutes < 1) return 'Hace instantes';
  if (diffMinutes < 60) return `Hace ${diffMinutes} min`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `Hace ${diffHours} h`;

  const diffDays = Math.floor(diffHours / 24);
  return `Hace ${diffDays} día${diffDays === 1 ? '' : 's'}`;
};

export default function HomePage() {
  const { sucursalActiva, loading: loadingSucursal } = useSucursal();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState({
    totalUsuarios: 0,
    usuariosActivos: 0,
    reservasHoy: 0,
    ingresosDelMes: 0,
  });
  const [vencimientos, setVencimientos] = useState<VencimientoUsuario[]>([]);
  const [reservasHoy, setReservasHoy] = useState<ReservaHoy[]>([]);
  const [planDistribution, setPlanDistribution] = useState<PlanItem[]>([]);
  const [recentActivities, setRecentActivities] = useState<Activity[]>([]);

  useEffect(() => {
    if (!loadingSucursal && sucursalActiva?.id) {
      cargarDashboard();
    }
  }, [loadingSucursal, sucursalActiva?.id]);

  const cargarDashboard = async () => {
    if (!sucursalActiva?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const hoy = new Date();
      const hoyStr = hoy.toISOString().split('T')[0];
      const inicioMes = new Date(hoy.getFullYear(), hoy.getMonth(), 1).toISOString();
      const diaSemana = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'][hoy.getDay()];
      const en30dias = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

      const [usuariosRes, pagosRes, horariosRes, reservasRes] = await Promise.all([
        supabase
          .from('usuarios')
          .select('id, nombre, estado, tipo_membresia, fecha_vencimiento, email, telefono', { count: 'exact' })
          .eq('sucursal_id', sucursalActiva.id),
        supabase
          .from('pagos')
          .select('id, monto, created_at, usuario_id')
          .eq('sucursal_id', sucursalActiva.id)
          .eq('estado', 'completado')
          .gte('fecha_pago', inicioMes),
        supabase
          .from('horarios')
          .select('id, hora_inicio, hora_fin, capacidad_maxima')
          .eq('sucursal_id', sucursalActiva.id)
          .eq('activo', true)
          .eq('dia_semana', diaSemana),
        supabase
          .from('reservas')
          .select('id, horario_id, usuario_id, estado, created_at')
          .eq('fecha_reserva', hoyStr)
          .in('estado', ['confirmada', 'pendiente']),
      ]);

      if (usuariosRes.error) throw usuariosRes.error;
      if (pagosRes.error) throw pagosRes.error;
      if (horariosRes.error) throw horariosRes.error;
      if (reservasRes.error) throw reservasRes.error;

      const usuarios = usuariosRes.data || [];
      const pagos = pagosRes.data || [];
      const horarios = horariosRes.data || [];
      const reservas = (reservasRes.data || []).filter((r) => horarios.some((h) => h.id === r.horario_id));

      const usuariosActivos = usuarios.filter((u) => u.estado === 'activo').length;
      const ingresosMes = pagos.reduce((sum, p) => sum + Number(p.monto), 0);

      setStats({
        totalUsuarios: usuariosRes.count || 0,
        usuariosActivos,
        reservasHoy: reservas.length,
        ingresosDelMes: ingresosMes,
      });

      const vencimientosActivos = usuarios
        .filter((u) => u.estado === 'activo' && u.fecha_vencimiento && u.fecha_vencimiento >= hoyStr && u.fecha_vencimiento <= en30dias)
        .sort((a, b) => new Date(a.fecha_vencimiento).getTime() - new Date(b.fecha_vencimiento).getTime())
        .slice(0, 8) as VencimientoUsuario[];
      setVencimientos(vencimientosActivos);

      const reservasPorHorario = horarios
        .map((h) => {
          const reserved = reservas.filter((r) => r.horario_id === h.id).length;
          const capacity = Math.max(1, Number(h.capacidad_maxima) || 1);
          const percentage = Math.round((reserved / capacity) * 100);

          return {
            id: h.id,
            time: `${h.hora_inicio?.slice(0, 5)} - ${h.hora_fin?.slice(0, 5)}`,
            reserved,
            capacity,
            percentage,
          };
        })
        .sort((a, b) => a.time.localeCompare(b.time))
        .slice(0, 8);
      setReservasHoy(reservasPorHorario);

      const planMap = new Map<string, number>();
      usuarios.forEach((u) => {
        const plan = u.tipo_membresia?.trim() || 'Sin plan';
        planMap.set(plan, (planMap.get(plan) || 0) + 1);
      });

      const plans = Array.from(planMap.entries())
        .map(([name, value], index) => ({
          name,
          value,
          color: PLAN_COLORS[index % PLAN_COLORS.length],
        }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 6);
      setPlanDistribution(plans);

      const recentReservas = await supabase
        .from('reservas')
        .select('id, usuario_id, estado, created_at, horarios!inner(sucursal_id)')
        .eq('horarios.sucursal_id', sucursalActiva.id)
        .order('created_at', { ascending: false })
        .limit(10);

      const recentPagos = await supabase
        .from('pagos')
        .select('id, usuario_id, monto, created_at')
        .eq('sucursal_id', sucursalActiva.id)
        .order('created_at', { ascending: false })
        .limit(10);

      const recentUsuarios = await supabase
        .from('usuarios')
        .select('id, nombre, created_at')
        .eq('sucursal_id', sucursalActiva.id)
        .order('created_at', { ascending: false })
        .limit(10);

      const userIds = new Set<string>();
      recentReservas.data?.forEach((r) => userIds.add(r.usuario_id));
      recentPagos.data?.forEach((p) => userIds.add(p.usuario_id));

      const usuariosMap = new Map<string, string>();
      if (userIds.size > 0) {
        const { data } = await supabase
          .from('usuarios')
          .select('id, nombre')
          .eq('sucursal_id', sucursalActiva.id)
          .in('id', Array.from(userIds));

        (data || []).forEach((u) => usuariosMap.set(u.id, u.nombre));
      }

      const activities: Activity[] = [
        ...(recentReservas.data || []).map((r) => ({
          id: `res-${r.id}`,
          type: 'reservation' as const,
          user: usuariosMap.get(r.usuario_id) || 'Usuario',
          action:
            r.estado === 'cancelada'
              ? 'canceló una reserva'
              : r.estado === 'confirmada'
              ? 'confirmó una reserva'
              : 'creó una reserva',
          time: getTimeAgo(r.created_at),
        })),
        ...(recentPagos.data || []).map((p) => ({
          id: `pay-${p.id}`,
          type: 'payment' as const,
          user: usuariosMap.get(p.usuario_id) || 'Usuario',
          action: `registró un pago de $${Number(p.monto).toLocaleString('es-CL')}`,
          time: getTimeAgo(p.created_at),
        })),
        ...(recentUsuarios.data || []).map((u) => ({
          id: `usr-${u.id}`,
          type: 'user' as const,
          user: u.nombre,
          action: 'se registró en el gimnasio',
          time: getTimeAgo(u.created_at),
        })),
      ]
        .sort((a, b) => {
          const aMinutes = Number(a.time.match(/\d+/)?.[0] || 0);
          const bMinutes = Number(b.time.match(/\d+/)?.[0] || 0);
          return aMinutes - bMinutes;
        })
        .slice(0, 10);

      setRecentActivities(activities);
    } catch (err: any) {
      console.error('Error cargando dashboard:', err);
      setError(err?.message || 'Error al cargar dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getDiasRestantes = (fechaVenc: string) => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const venc = new Date(`${fechaVenc}T00:00:00`);
    return Math.ceil((venc.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24));
  };

  const getEstadoVencimiento = (dias: number) => {
    if (dias <= 3) return { label: 'Crítico', bg: 'bg-red-100', text: 'text-red-700' };
    if (dias <= 7) return { label: 'Urgente', bg: 'bg-orange-100', text: 'text-orange-700' };
    if (dias <= 14) return { label: 'Próximo', bg: 'bg-amber-100', text: 'text-amber-700' };
    return { label: 'Normal', bg: 'bg-teal-100', text: 'text-teal-700' };
  };

  if (loadingSucursal || loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mb-4"></div>
            <p className="text-gray-600">Cargando dashboard...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!sucursalActiva) {
    return (
      <AdminLayout>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
          <h3 className="text-lg font-semibold text-amber-800 mb-2">Sin sucursal seleccionada</h3>
          <p className="text-amber-600">Selecciona una sucursal para continuar</p>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h3 className="text-lg font-semibold text-red-800 mb-2">Error al cargar</h3>
          <p className="text-red-600 mb-4">{error}</p>
          <button onClick={cargarDashboard} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
            Reintentar
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <MetricCard title="Total Usuarios" value={String(stats.totalUsuarios)} icon="ri-user-line" color="blue" />
          <MetricCard title="Usuarios Activos" value={String(stats.usuariosActivos)} icon="ri-user-star-line" color="teal" />
          <MetricCard title="Reservas Hoy" value={String(stats.reservasHoy)} icon="ri-calendar-check-line" color="purple" />
          <MetricCard
            title="Ingresos del Mes"
            value={`$${stats.ingresosDelMes.toLocaleString('es-CL')}`}
            icon="ri-money-dollar-circle-line"
            color="green"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-100 p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Reservas de Hoy</h3>
            <div className="space-y-4">
              {reservasHoy.length === 0 ? (
                <p className="text-sm text-gray-500">No hay horarios registrados para hoy.</p>
              ) : (
                reservasHoy.map((res) => (
                  <div key={res.id} className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-full bg-teal-50 flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-teal-600 text-sm"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{res.time}</p>
                      <p className="text-xs text-gray-500">
                        {res.reserved}/{res.capacity} cupos reservados
                      </p>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="w-32 h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            res.percentage >= 100
                              ? 'bg-red-500'
                              : res.percentage >= 90
                              ? 'bg-orange-500'
                              : res.percentage >= 70
                              ? 'bg-teal-500'
                              : 'bg-teal-400'
                          }`}
                          style={{ width: `${Math.min(res.percentage, 100)}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium text-gray-700 w-10 text-right">{res.percentage}%</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Distribución de Planes</h3>
            <div className="space-y-4">
              {planDistribution.length === 0 ? (
                <p className="text-sm text-gray-500">Sin datos de planes.</p>
              ) : (
                planDistribution.map((plan) => (
                  <div key={plan.name}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm text-gray-700">{plan.name}</span>
                      <span className="text-sm font-semibold text-gray-900">{plan.value}</span>
                    </div>
                    <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${(plan.value / Math.max(1, stats.totalUsuarios)) * 100}%`,
                          backgroundColor: plan.color,
                        }}
                      ></div>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-between">
              <span className="text-sm text-gray-600">Total Usuarios</span>
              <span className="text-lg font-bold text-gray-900">{stats.totalUsuarios}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-100 p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Próximos Vencimientos</h3>
            <div className="space-y-3">
              {vencimientos.length === 0 ? (
                <p className="text-sm text-gray-500">No hay membresías por vencer en los próximos 30 días.</p>
              ) : (
                vencimientos.map((usuario) => {
                  const dias = getDiasRestantes(usuario.fecha_vencimiento);
                  const estado = getEstadoVencimiento(dias);
                  return (
                    <div key={usuario.id} className="flex items-center gap-4 p-3 rounded-lg border border-gray-100">
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-semibold text-gray-600">
                          {usuario.nombre?.charAt(0)?.toUpperCase() || 'U'}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{usuario.nombre}</p>
                        <p className="text-xs text-gray-500 truncate">{usuario.tipo_membresia || 'Sin plan'}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-semibold text-gray-900">
                          {dias === 0 ? 'Hoy' : dias === 1 ? 'Mañana' : `${dias} días`}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(`${usuario.fecha_vencimiento}T00:00:00`).toLocaleDateString('es-CL', {
                            day: '2-digit',
                            month: 'short',
                          })}
                        </p>
                      </div>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${estado.bg} ${estado.text}`}>
                        {estado.label}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Actividad Reciente</h3>
            <div className="space-y-4">
              {recentActivities.length === 0 ? (
                <p className="text-sm text-gray-500">Sin actividad reciente.</p>
              ) : (
                recentActivities.map((activity) => (
                  <div key={activity.id} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-teal-50 flex items-center justify-center flex-shrink-0">
                      <i
                        className={`text-sm ${
                          activity.type === 'payment'
                            ? 'ri-money-dollar-circle-line text-green-600'
                            : activity.type === 'user'
                            ? 'ri-user-add-line text-blue-600'
                            : 'ri-bookmark-line text-teal-600'
                        }`}
                      ></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{activity.user}</p>
                      <p className="text-xs text-gray-500">{activity.action}</p>
                    </div>
                    <span className="text-xs text-gray-400 flex-shrink-0 whitespace-nowrap">{activity.time}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

function MetricCard({
  title,
  value,
  icon,
  color,
}: {
  title: string;
  value: string;
  icon: string;
  color: 'blue' | 'teal' | 'purple' | 'green';
}) {
  const colorMap = {
    blue: 'bg-blue-100 text-blue-600',
    teal: 'bg-teal-100 text-teal-600',
    purple: 'bg-purple-100 text-purple-600',
    green: 'bg-green-100 text-green-600',
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-100">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600 mb-1">{title}</p>
          <p className="text-3xl font-bold text-gray-900">{value}</p>
        </div>
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colorMap[color]}`}>
          <i className={`${icon} text-2xl`}></i>
        </div>
      </div>
    </div>
  );
}
